import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { Map as MapLibreMap } from "maplibre-gl";
import type { Listing, ListingsResponse, MapRegion, VoteChoice } from "./types";

const storage = {
  name: "rental-room-name",
};

const voteLabels: Record<VoteChoice, string> = {
  keep: "Keep",
  maybe: "Maybe",
  ditch: "Ditch",
};

const regionSourceId = "drawn-region";
const regionFillId = "drawn-region-fill";
const regionLineId = "drawn-region-line";
const listingsSourceId = "rental-listings";
const listingsCircleId = "rental-listings-circle";
const listingsLabelId = "rental-listings-label";

function priceLabel(price: number) {
  return `$${(price / 1000).toFixed(price % 1000 ? 1 : 0)}k`;
}

function listingsCollection(listings: Listing[], selectedId: string | null) {
  return {
    type: "FeatureCollection" as const,
    features: listings
      .filter((listing) => Number.isFinite(listing.latitude) && Number.isFinite(listing.longitude))
      .map((listing) => ({
        type: "Feature" as const,
        properties: {
          id: listing.id,
          priceLabel: priceLabel(listing.price),
          decision: decisionFor(listing),
          selected: listing.id === selectedId ? 1 : 0,
        },
        geometry: {
          type: "Point" as const,
          coordinates: [listing.longitude, listing.latitude],
        },
      })),
  };
}

function ensureListingsLayers(map: MapLibreMap) {
  if (!map.getSource(listingsSourceId)) {
    map.addSource(listingsSourceId, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      promoteId: "id",
    });
  }
  if (!map.getLayer(listingsCircleId)) {
    map.addLayer({
      id: listingsCircleId,
      type: "circle",
      source: listingsSourceId,
      paint: {
        "circle-radius": [
          "case",
          ["==", ["get", "selected"], 1], 16,
          13,
        ],
        "circle-color": [
          "case",
          ["==", ["get", "selected"], 1], "#ffffff",
          ["==", ["get", "decision"], "ditched"], "#8a5547",
          ["==", ["get", "decision"], "mutual"], "#2c5749",
          "#17352d",
        ],
        "circle-stroke-width": 2,
        "circle-stroke-color": [
          "case",
          ["==", ["get", "selected"], 1], "#17352d",
          "#f6faf7",
        ],
      },
    });
  }
  if (!map.getLayer(listingsLabelId)) {
    map.addLayer({
      id: listingsLabelId,
      type: "symbol",
      source: listingsSourceId,
      layout: {
        "text-field": ["get", "priceLabel"],
        "text-size": 11,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": [
          "case",
          ["==", ["get", "selected"], 1], "#10251f",
          "#ffffff",
        ],
      },
    });
  }
}

function paintListings(map: MapLibreMap, listings: Listing[], selectedId: string | null) {
  ensureListingsLayers(map);
  const source = map.getSource(listingsSourceId) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData(listingsCollection(listings, selectedId));
}

function money(value: number) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(value);
}

function shortDate(value: string) {
  if (!value) return "Recently listed";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-CA", { month: "short", day: "numeric" }).format(date);
}

function decisionFor(listing: Listing) {
  const choices = listing.votes.map((vote) => vote.choice);
  if (choices.length >= 2 && choices.every((choice) => choice === "keep")) return "mutual";
  if (choices.includes("keep") && choices.includes("ditch")) return "split";
  if (choices.length >= 2 && choices.every((choice) => choice === "ditch")) return "ditched";
  if (choices.includes("maybe")) return "maybe";
  return "open";
}

function listingInRegion(listing: Listing, region: MapRegion | undefined | null) {
  if (!region) return true;
  const { north, south, east, west } = region;
  return listing.latitude <= north
    && listing.latitude >= south
    && listing.longitude <= east
    && listing.longitude >= west;
}

function normalizeClientRegion(region: MapRegion): MapRegion | null {
  const north = Math.max(region.north, region.south);
  const south = Math.min(region.north, region.south);
  const east = Math.max(region.east, region.west);
  const west = Math.min(region.east, region.west);
  if (![north, south, east, west].every(Number.isFinite)) return null;
  if (Math.abs(north - south) < 0.0005 || Math.abs(east - west) < 0.0005) return null;
  return { north, south, east, west };
}

function regionPolygon(region: MapRegion) {
  const { north, south, east, west } = region;
  return {
    type: "Feature" as const,
    properties: {},
    geometry: {
      type: "Polygon" as const,
      coordinates: [[
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
      ]],
    },
  };
}

function boundsFromPoints(a: maplibregl.LngLat, b: maplibregl.LngLat): MapRegion {
  return {
    north: Math.max(a.lat, b.lat),
    south: Math.min(a.lat, b.lat),
    east: Math.max(a.lng, b.lng),
    west: Math.min(a.lng, b.lng),
  };
}

function Onboarding({ onEnter }: { onEnter: (name: string) => void }) {
  const [name, setName] = useState(localStorage.getItem(storage.name) ?? "");

  return (
    <main className="onboarding-shell">
      <section className="onboarding" aria-labelledby="welcome-title">
        <div className="door-mark" aria-hidden="true">
          <span />
          <span />
        </div>
        <p className="product-name">Rental Room</p>
        <h1 id="welcome-title">A shared shortlist, without the group chat archaeology.</h1>
        <p className="welcome-copy">Enter your name once. Your choices stay separate and easy to compare.</p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) onEnter(name.trim());
          }}
        >
          <label>
            Your name
            <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required />
          </label>
          <button className="primary-action" type="submit">Enter the shortlist</button>
        </form>
      </section>
    </main>
  );
}

function SearchSummary({ data, visibleCount, hasCustomArea }: {
  data: ListingsResponse;
  visibleCount: number;
  hasCustomArea: boolean;
}) {
  const filters = data.filters;
  const areaLabel = hasCustomArea ? "Custom map area" : filters.area;
  const items = [areaLabel, filters.price, filters.bedrooms, filters.freshness].filter(Boolean);
  return (
    <div className="search-summary" aria-label="Current rental search">
      <div>
        <p className="summary-title">Current search</p>
        <p>{items.join(" · ") || "Saved HouseSigma rental search"}</p>
      </div>
      <div className="freshness">
        <span>{visibleCount}</span>
        <small>matching homes</small>
      </div>
    </div>
  );
}

function VoteRail({ listing, userName, onVote }: { listing: Listing; userName: string; onVote: (choice: VoteChoice) => void }) {
  const mine = listing.votes.find((vote) => vote.userName.toLowerCase() === userName.toLowerCase())?.choice;
  return (
    <div className="vote-area">
      <div className="vote-rail" aria-label={`Your vote for ${listing.address}`}>
        {(["keep", "maybe", "ditch"] as VoteChoice[]).map((choice) => (
          <button
            key={choice}
            className={mine === choice ? "selected" : ""}
            data-choice={choice}
            onClick={(event) => {
              event.stopPropagation();
              onVote(choice);
            }}
            aria-pressed={mine === choice}
          >
            {voteLabels[choice]}
          </button>
        ))}
      </div>
      <div className="other-votes">
        {listing.votes.length === 0 ? (
          <span>No decisions yet</span>
        ) : (
          listing.votes.map((vote) => (
            <span key={vote.userName} data-choice={vote.choice}>
              <b>{vote.userName}</b> {voteLabels[vote.choice].toLowerCase()}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function ListingRow({ listing, userName, selected, onSelect, onVote }: {
  listing: Listing;
  userName: string;
  selected: boolean;
  onSelect: () => void;
  onVote: (choice: VoteChoice) => void;
}) {
  const decision = decisionFor(listing);
  return (
    <article className={`listing-row ${selected ? "is-selected" : ""}`} data-decision={decision}>
      <button className="listing-photo" onClick={onSelect} aria-label={`View ${listing.address}`}>
        <img src={listing.images[0]} alt={listing.address} loading="lazy" />
        <span className="photo-count">{listing.images.length} photos</span>
      </button>
      <div className="listing-copy">
        <button className="listing-heading" onClick={onSelect}>
          <span className="price">{money(listing.price)}<small>/mo</small></span>
          <h2>{listing.address}</h2>
          <p>{listing.neighborhood || listing.city}</p>
        </button>
        <div className="facts" aria-label="Listing facts">
          <span>{listing.bedrooms} bed</span>
          <span>{listing.bathrooms} bath</span>
          <span>{listing.garage} parking</span>
          <span>{listing.size}</span>
        </div>
        <div className="row-foot">
          <span>{listing.propertyType}</span>
          <span>Listed {shortDate(listing.listedAt)}</span>
        </div>
        <VoteRail listing={listing} userName={userName} onVote={onVote} />
      </div>
    </article>
  );
}

function ensureRegionLayers(map: MapLibreMap) {
  if (!map.getSource(regionSourceId)) {
    map.addSource(regionSourceId, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }
  if (!map.getLayer(regionFillId)) {
    map.addLayer({
      id: regionFillId,
      type: "fill",
      source: regionSourceId,
      paint: {
        "fill-color": "#17352d",
        "fill-opacity": 0.14,
      },
    });
  }
  if (!map.getLayer(regionLineId)) {
    map.addLayer({
      id: regionLineId,
      type: "line",
      source: regionSourceId,
      paint: {
        "line-color": "#17352d",
        "line-width": 2,
        "line-opacity": 0.9,
      },
    });
  }
}

function paintRegion(map: MapLibreMap, region: MapRegion | null) {
  ensureRegionLayers(map);
  const source = map.getSource(regionSourceId) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData({
    type: "FeatureCollection",
    features: region ? [regionPolygon(region)] : [],
  });
}

function RentalMap({ listings, selectedId, region, drawing, onSelect, onDraftRegion, onRegionDrawn }: {
  listings: Listing[];
  selectedId: string | null;
  region: MapRegion | undefined | null;
  drawing: boolean;
  onSelect: (id: string) => void;
  onDraftRegion: (region: MapRegion | null) => void;
  onRegionDrawn: (region: MapRegion) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const drawingRef = useRef(drawing);
  const regionRef = useRef(region);
  const listingsRef = useRef(listings);
  const selectedIdRef = useRef(selectedId);
  const onDraftRegionRef = useRef(onDraftRegion);
  const onRegionDrawnRef = useRef(onRegionDrawn);
  const onSelectRef = useRef(onSelect);
  const dragOriginRef = useRef<maplibregl.LngLat | null>(null);
  const fittedRef = useRef(false);
  const lastRegionKeyRef = useRef<string>("");

  useEffect(() => { drawingRef.current = drawing; }, [drawing]);
  useEffect(() => { regionRef.current = region; }, [region]);
  useEffect(() => { listingsRef.current = listings; }, [listings]);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { onDraftRegionRef.current = onDraftRegion; }, [onDraftRegion]);
  useEffect(() => { onRegionDrawnRef.current = onRegionDrawn; }, [onRegionDrawn]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      center: [-79.3922, 43.6479],
      zoom: 14.1,
      attributionControl: false,
      style: {
        version: 8,
        glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
        sources: {
          carto: {
            type: "raster",
            tiles: [
              "https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
              "https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
              "https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png",
            ],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors © CARTO",
          },
        },
        layers: [{ id: "carto-voyager", type: "raster", source: "carto" }],
      },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");

    const onLoad = () => {
      ensureRegionLayers(map);
      ensureListingsLayers(map);
      paintRegion(map, regionRef.current ?? null);
      paintListings(map, listingsRef.current, selectedIdRef.current);
      map.on("click", listingsCircleId, onListingClick);
      map.on("click", listingsLabelId, onListingClick);
      map.on("mouseenter", listingsCircleId, setPointer);
      map.on("mouseleave", listingsCircleId, clearPointer);
    };
    map.on("load", onLoad);

    const onMouseDown = (event: maplibregl.MapMouseEvent) => {
      if (!drawingRef.current) return;
      event.preventDefault();
      dragOriginRef.current = event.lngLat;
      map.dragPan.disable();
      onDraftRegionRef.current(boundsFromPoints(event.lngLat, event.lngLat));
    };
    const onMouseMove = (event: maplibregl.MapMouseEvent) => {
      if (!drawingRef.current || !dragOriginRef.current) return;
      onDraftRegionRef.current(boundsFromPoints(dragOriginRef.current, event.lngLat));
    };
    const finishDrag = (event: maplibregl.MapMouseEvent) => {
      if (!drawingRef.current || !dragOriginRef.current) return;
      const raw = boundsFromPoints(dragOriginRef.current, event.lngLat);
      dragOriginRef.current = null;
      map.dragPan.enable();
      const next = normalizeClientRegion(raw);
      if (!next) {
        onDraftRegionRef.current(null);
        return;
      }
      onDraftRegionRef.current(null);
      onRegionDrawnRef.current(next);
    };
    const cancelDrag = () => {
      if (!dragOriginRef.current) return;
      dragOriginRef.current = null;
      map.dragPan.enable();
      onDraftRegionRef.current(null);
    };
    const onListingClick = (event: maplibregl.MapLayerMouseEvent) => {
      if (drawingRef.current) return;
      const id = event.features?.[0]?.properties?.id;
      if (typeof id === "string" && id) onSelectRef.current(id);
    };
    const setPointer = () => { map.getCanvas().style.cursor = drawingRef.current ? "crosshair" : "pointer"; };
    const clearPointer = () => { map.getCanvas().style.cursor = drawingRef.current ? "crosshair" : ""; };

    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);
    map.on("mouseup", finishDrag);
    map.getContainer().addEventListener("mouseleave", cancelDrag);

    mapRef.current = map;
    const observer = new ResizeObserver(() => {
      map.resize();
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      map.getContainer().removeEventListener("mouseleave", cancelDrag);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => paintRegion(map, region ?? null);
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [region]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !region || drawing) return;
    const key = `${region.west},${region.south},${region.east},${region.north}`;
    if (key === lastRegionKeyRef.current) return;
    lastRegionKeyRef.current = key;
    map.fitBounds(
      [[region.west, region.south], [region.east, region.north]],
      { padding: 48, maxZoom: 15.5, duration: 420 },
    );
  }, [drawing, region]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = drawing ? "crosshair" : "";
    if (!drawing) {
      dragOriginRef.current = null;
      map.dragPan.enable();
    }
  }, [drawing]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const visible = region
        ? listings.filter((listing) => listingInRegion(listing, region))
        : listings;
      paintListings(map, visible, selectedId);
      if (!region && !fittedRef.current && visible.length > 1) {
        const bounds = new maplibregl.LngLatBounds();
        visible.forEach((listing) => bounds.extend([listing.longitude, listing.latitude]));
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 70, maxZoom: 15.3, duration: 450 });
          fittedRef.current = true;
        }
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [listings, region, selectedId]);

  useEffect(() => {
    const listing = listings.find((item) => item.id === selectedId);
    if (listing && mapRef.current && !drawing) {
      mapRef.current.easeTo({ center: [listing.longitude, listing.latitude], zoom: 15.4, duration: 420 });
    }
  }, [listings, selectedId, drawing]);

  return (
    <div
      ref={containerRef}
      className={`map-canvas ${drawing ? "is-drawing" : ""}`}
      aria-label="Map of matching rentals"
    />
  );
}

function DetailSheet({ listing, userName, onClose, onVote }: {
  listing: Listing;
  userName: string;
  onClose: () => void;
  onVote: (choice: VoteChoice) => void;
}) {
  const [photo, setPhoto] = useState(0);
  const details = Object.entries(listing.details).filter(([label, value]) => value && !["Data Source:", "Listing Brokerage:"].includes(label)).slice(0, 14);
  return (
    <div className="sheet-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="detail-sheet" role="dialog" aria-modal="true" aria-labelledby="detail-title">
        <button className="close-sheet" onClick={onClose} aria-label="Close listing details">Close</button>
        <div className="gallery">
          <img src={listing.images[photo]} alt={`${listing.address}, photo ${photo + 1}`} />
          <div className="gallery-nav">
            <button onClick={() => setPhoto((photo - 1 + listing.images.length) % listing.images.length)} aria-label="Previous photo">Previous</button>
            <span>{photo + 1} / {listing.images.length}</span>
            <button onClick={() => setPhoto((photo + 1) % listing.images.length)} aria-label="Next photo">Next</button>
          </div>
        </div>
        <div className="detail-copy">
          <p className="detail-price">{money(listing.price)}<small>/month</small></p>
          <h2 id="detail-title">{listing.address}</h2>
          <p className="detail-place">{listing.neighborhood}, {listing.city}</p>
          <VoteRail listing={listing} userName={userName} onVote={onVote} />
          <div className="detail-facts">
            {details.map(([label, value]) => (
              <div key={`${label}-${value}`}>
                <span>{label.replace(/:$/, "")}</span>
                <b>{typeof value === "string" || typeof value === "number" ? value : ""}</b>
              </div>
            ))}
          </div>
          {typeof listing.description === "string" && listing.description.trim() && !listing.description.includes("[object Object]") ? (
            <p className="description">{listing.description}</p>
          ) : null}
          <a className="source-link" href={listing.url} target="_blank" rel="noreferrer">Open the full HouseSigma listing</a>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const [userName, setUserName] = useState(localStorage.getItem(storage.name) ?? "");
  const [data, setData] = useState<ListingsResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [mobileView, setMobileView] = useState<"list" | "map">("list");
  const [drawing, setDrawing] = useState(false);
  const [draftRegion, setDraftRegion] = useState<MapRegion | null>(null);
  const [savingRegion, setSavingRegion] = useState(false);
  const savingRegionRef = useRef(false);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/listings");
      if (!response.ok) throw new Error("The shortlist could not be loaded.");
      const nextData = await response.json() as ListingsResponse;
      setData((current) => {
        if (savingRegionRef.current && current?.filters.region && !nextData.filters.region) {
          return {
            ...nextData,
            filters: { ...nextData.filters, region: current.filters.region },
          };
        }
        return nextData;
      });
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The shortlist could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userName) void loadListings();
  }, [loadListings, userName]);

  const savedRegion = data?.filters.region;
  const activeRegion = draftRegion ?? savedRegion ?? null;

  const listings = useMemo(() => {
    if (!data) return [];
    return data.listings.filter((listing) => (
      listingInRegion(listing, activeRegion)
      && (decisionFilter === "all" || decisionFor(listing) === decisionFilter)
    ));
  }, [activeRegion, data, decisionFilter]);

  const selected = listings.find((listing) => listing.id === selectedId) ?? null;

  useEffect(() => {
    if (selectedId && !listings.some((listing) => listing.id === selectedId)) {
      setSelectedId(null);
    }
  }, [listings, selectedId]);

  const saveRegion = useCallback(async (next: MapRegion | null) => {
    setSavingRegion(true);
    savingRegionRef.current = true;
    setDraftRegion(null);
    setDrawing(false);
    setData((current) => {
      if (!current) return current;
      const filters = next
        ? { ...current.filters, region: next }
        : Object.fromEntries(Object.entries(current.filters).filter(([key]) => key !== "region")) as ListingsResponse["filters"];
      return { ...current, filters };
    });
    try {
      const response = await fetch("/api/region", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ region: next }),
      });
      if (!response.ok) throw new Error("The map area could not be saved.");
      const payload = await response.json() as { filters?: ListingsResponse["filters"] };
      if (payload.filters) {
        setData((current) => current ? { ...current, filters: payload.filters! } : current);
      }
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The map area could not be saved.");
      await loadListings();
    } finally {
      savingRegionRef.current = false;
      setSavingRegion(false);
    }
  }, [loadListings]);

  const vote = async (listingId: string, choice: VoteChoice) => {
    if (!data) return;
    const now = new Date().toISOString();
    setData({
      ...data,
      listings: data.listings.map((listing) => listing.id !== listingId ? listing : {
        ...listing,
        votes: [...listing.votes.filter((item) => item.userName.toLowerCase() !== userName.toLowerCase()), { userName, choice, updatedAt: now }],
      }),
    });
    const response = await fetch("/api/votes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ listingId, userName, choice }),
    });
    if (!response.ok) {
      setError("Your choice did not save. Please try again.");
      await loadListings();
    }
  };

  if (!userName) {
    return <Onboarding onEnter={(name) => {
      localStorage.setItem(storage.name, name);
      setUserName(name);
    }} />;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand-line"><span className="mini-door" aria-hidden="true" />Rental Room</div>
        <div className="person-control">
          <span>Viewing as <b>{userName}</b></span>
          <button onClick={() => { localStorage.removeItem(storage.name); setUserName(""); }}>Switch name</button>
        </div>
      </header>
      {error && <div className="error-banner" role="alert">{error}</div>}
      {loading && !data ? (
        <main className="loading-state">Loading the shortlist…</main>
      ) : data ? (
        <main>
          <SearchSummary data={data} visibleCount={listings.length} hasCustomArea={Boolean(activeRegion)} />
          <div className="toolbar">
            <div className="decision-filters" aria-label="Filter by decision">
              {[
                ["all", "All"], ["mutual", "Both keep"], ["maybe", "On the fence"], ["split", "Split"], ["ditched", "Both ditch"],
              ].map(([value, label]) => (
                <button key={value} onClick={() => setDecisionFilter(value)} aria-pressed={decisionFilter === value}>{label}</button>
              ))}
            </div>
            <div className="mobile-switch" aria-label="Choose list or map view">
              <button onClick={() => setMobileView("list")} aria-pressed={mobileView === "list"}>List</button>
              <button onClick={() => setMobileView("map")} aria-pressed={mobileView === "map"}>Map</button>
            </div>
          </div>
          <div className="workspace" data-mobile-view={mobileView}>
            <section className="listing-list" aria-label="Matching rental listings">
              {listings.length ? listings.map((listing) => (
                <ListingRow
                  key={listing.id}
                  listing={listing}
                  userName={userName}
                  selected={selectedId === listing.id}
                  onSelect={() => setSelectedId(listing.id)}
                  onVote={(choice) => void vote(listing.id, choice)}
                />
              )) : <p className="empty-state">No homes match this area or decision view yet.</p>}
            </section>
            <aside className="map-panel">
              <div className="map-tools" aria-label="Map area tools">
                <button
                  type="button"
                  className={drawing ? "is-active" : ""}
                  aria-pressed={drawing}
                  disabled={savingRegion}
                  onClick={() => {
                    setMobileView("map");
                    setDraftRegion(null);
                    setDrawing((value) => !value);
                  }}
                >
                  {drawing ? "Drag on the map…" : "Draw area"}
                </button>
                {savedRegion || draftRegion ? (
                  <button
                    type="button"
                    disabled={savingRegion}
                    onClick={() => {
                      setDraftRegion(null);
                      void saveRegion(null);
                    }}
                  >
                    Clear area
                  </button>
                ) : null}
              </div>
              <RentalMap
                listings={listings}
                selectedId={selectedId}
                region={activeRegion}
                drawing={drawing}
                onSelect={(id) => {
                  if (drawing) return;
                  setSelectedId(id);
                }}
                onDraftRegion={setDraftRegion}
                onRegionDrawn={(next) => void saveRegion(next)}
              />
              {selected && !drawing && (
                <button className="map-selection" onClick={() => setSelectedId(selected.id)}>
                  <img src={selected.images[0]} alt="" />
                  <span><b>{money(selected.price)}</b>{selected.address}</span>
                </button>
              )}
            </aside>
          </div>
        </main>
      ) : null}
      {selected && <DetailSheet listing={selected} userName={userName} onClose={() => setSelectedId(null)} onVote={(choice) => void vote(selected.id, choice)} />}
    </div>
  );
}
